import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SeatMapView, useSeatSelection } from '@bre-ticketing/seatmap';
import type { ManifestGeometry } from '@bre-ticketing/seatmap';
import type { ManifestLayout, PriceZoneRow, PricingTierRow, TicketTypeRow } from '../../api';
import { endpoints, eventEndpoints } from '../../api';
import { Badge, Button, EmptyRow, Field, Panel, Table } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, humanize, utcToZoned, zonedToUtc } from './shared';
import { dateWords } from '../../terms';

const CATEGORIES = ['ADULT', 'CHILD', 'SENIOR', 'STUDENT', 'MILITARY', 'VIP', 'MEMBER', 'GROUP', 'PROMO', 'COMP', 'OTHER'];
const CHANNELS = ['WEB', 'BOX_OFFICE', 'PHONE', 'GROUP_SALES'];
const ZONE_PALETTE = ['#7b1e2b', '#2f6fb3', '#1d7a4c', '#b3893f', '#8a5fc9', '#d99a2b', '#3d8fd6', '#a35a66'];

/**
 * Ticket types, price zones and pricing tiers. Every row can be edited in
 * place, because the thing an operator forgets (a channel, a max per order)
 * is exactly the thing they come back for. Zones are painted onto the seat
 * map seat by seat, row by row, or section by section; the most specific
 * assignment wins, so part of a section can sit in one zone and the rest in
 * another.
 */
export function TicketsPanel({ detail, refresh, onError, can }: PanelProps) {
  const editable = can('pricing.manage') && detail.event.status !== 'ARCHIVED';
  const [addingType, setAddingType] = useState(false);
  const [editingType, setEditingType] = useState<string | null>(null);
  const [addingZone, setAddingZone] = useState(false);
  const [editingZone, setEditingZone] = useState<string | null>(null);
  const [addingTier, setAddingTier] = useState(false);
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [painting, setPainting] = useState(false);

  const removeZone = useMutation({ mutationFn: (zoneId: string) => eventEndpoints.deletePriceZone(detail.event.id, zoneId), onSuccess: refresh, onError });
  // Retiring is the way out for a zone that price tables or orders still point
  // at, which cannot be deleted without rewriting them (D-156).
  const retireZone = useMutation({
    mutationFn: ({ zoneId, active }: { zoneId: string; active: boolean }) => eventEndpoints.updatePriceZone(detail.event.id, zoneId, { active }),
    onSuccess: refresh,
    onError,
  });
  const removeTier = useMutation({ mutationFn: (tierId: string) => eventEndpoints.deleteTier(detail.event.id, tierId), onSuccess: refresh, onError });
  const zones = useQuery({ queryKey: ['event-zones', detail.event.id], queryFn: () => eventEndpoints.priceZones(detail.event.id) });
  const types = useQuery({ queryKey: ['event-ticket-types', detail.event.id], queryFn: () => eventEndpoints.ticketTypes(detail.event.id) });
  const refreshAll = () => {
    void zones.refetch();
    void types.refetch();
    refresh();
  };

  return (
    <div className="space-y-4">
      {addingType && editable ? <TicketTypeForm eventId={detail.event.id} onDone={() => { setAddingType(false); refreshAll(); }} onCancel={() => setAddingType(false)} onError={onError} /> : null}
      <Panel
        title="Ticket types"
        description="Adult, child, VIP… Each gets its own price per zone. A type limited to some channels is hidden elsewhere."
        actions={editable && !addingType ? <Button variant="primary" onClick={() => setAddingType(true)}>Add a ticket type</Button> : null}
      >
        <Table headers={['Type', 'Category', 'Channels', 'Per order', 'Status', '']}>
          {(types.data?.ticket_types ?? []).length === 0 ? (
            <EmptyRow columns={6}>No ticket types yet.</EmptyRow>
          ) : (
            types.data!.ticket_types.map((t) =>
              editingType === t.id ? (
                <tr key={t.id} className="border-b border-line last:border-0">
                  <td colSpan={6} className="px-5 py-3">
                    <TicketTypeForm eventId={detail.event.id} existing={t} onDone={() => { setEditingType(null); refreshAll(); }} onCancel={() => setEditingType(null)} onError={onError} inline />
                  </td>
                </tr>
              ) : (
                <tr key={t.id} className="border-b border-line last:border-0">
                  <td className="px-5 py-3">
                    <div className="font-medium text-ink-900">{t.name}</div>
                    <div className="text-xs text-ink-400">{t.code}{t.is_comp_type ? ' · comp' : ''}{t.requires_access_code ? ' · access code' : ''}{t.timed_entry ? ' · timed entry' : ''}</div>
                  </td>
                  <td className="px-5 py-3 text-sm text-ink-600">{humanize(t.category)}</td>
                  <td className="px-5 py-3 text-sm text-ink-600">{(t.channels ?? []).length ? t.channels!.map(humanize).join(', ') : 'all'}</td>
                  <td className="tabular px-5 py-3 text-sm text-ink-600">{t.min_per_order}–{t.max_per_order ?? '∞'}</td>
                  <td className="px-5 py-3 text-sm">{t.active ? <span className="text-ok">active</span> : <span className="text-ink-400">inactive</span>}</td>
                  <td className="px-5 py-3 text-right">{editable ? <Button onClick={() => setEditingType(t.id)}>Edit</Button> : null}</td>
                </tr>
              ),
            )
          )}
        </Table>
      </Panel>

      {addingZone && editable ? <ZoneForm eventId={detail.event.id} nextColour={ZONE_PALETTE[(zones.data?.price_zones.length ?? 0) % ZONE_PALETTE.length]!} onDone={() => { setAddingZone(false); refreshAll(); }} onCancel={() => setAddingZone(false)} onError={onError} /> : null}
      <Panel
        title="Price zones"
        description="What shares a price (price levels in reports). Paint seats onto the map — a seat's own zone beats its row's, which beats its section's, so a section can straddle zones — and give each general-admission area a zone from the same screen. Nothing sells without one."
        actions={
          <div className="flex gap-2">
            {editable && (zones.data?.price_zones.length ?? 0) > 0 ? <Button variant={painting ? 'primary' : 'secondary'} onClick={() => setPainting(!painting)}>{painting ? 'Close zone map' : 'Paint zones on the map'}</Button> : null}
            {editable && !addingZone ? <Button variant="primary" onClick={() => setAddingZone(true)}>Add a zone</Button> : null}
          </div>
        }
      >
        <Table headers={['Zone', 'Assignments', '']}>
          {(zones.data?.price_zones ?? []).length === 0 ? (
            <EmptyRow columns={3}>No price zones yet. Even a single-price event needs one.</EmptyRow>
          ) : (
            zones.data!.price_zones.map((z) =>
              editingZone === z.id ? (
                <tr key={z.id} className="border-b border-line last:border-0">
                  <td colSpan={3} className="px-5 py-3">
                    <ZoneForm eventId={detail.event.id} existing={z} nextColour={z.color_hex ?? ZONE_PALETTE[0]!} onDone={() => { setEditingZone(null); refreshAll(); }} onCancel={() => setEditingZone(null)} onError={onError} inline />
                  </td>
                </tr>
              ) : (
                <tr key={z.id} className={`border-b border-line last:border-0 ${z.active === 0 ? 'opacity-60' : ''}`}>
                  <td className="px-5 py-3">
                    <span className="mr-2 inline-block h-3 w-3 rounded-sm align-middle" style={{ background: z.color_hex ?? '#d9d9d9' }} />
                    <span className="font-medium text-ink-900">{z.name}</span>
                    <span className="ml-2 text-xs text-ink-400">{z.code}</span>
                    {/* A retired zone stays for the price tables and reports
                        that still reference it, and leaves the website (D-156). */}
                    {z.active === 0 ? <Badge tone="neutral">retired</Badge> : null}
                  </td>
                  <td className="px-5 py-3 text-sm text-ink-600">{assignmentSummary(z)}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {editable ? (
                      <>
                        <Button onClick={() => setEditingZone(z.id)}>Edit</Button>
                        {z.active === 0 ? (
                          <Button className="ml-2" onClick={() => retireZone.mutate({ zoneId: z.id, active: true })}>
                            Restore
                          </Button>
                        ) : (
                          <Button
                            className="ml-2"
                            onClick={() => {
                              if (window.confirm(`Retire zone ${z.name}? It leaves the seat map, the price tables and the website, and stays on records that already reference it.`)) {
                                retireZone.mutate({ zoneId: z.id, active: false });
                              }
                            }}
                          >
                            Retire
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          className="ml-2"
                          onClick={() => {
                            if (window.confirm(`Delete zone ${z.name}? Only a zone that was never priced or sold can be deleted; otherwise retire it.`)) removeZone.mutate(z.id);
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ),
            )
          )}
        </Table>
      </Panel>

      {painting && editable && zones.data ? <ZoneMap detail={detail} zones={zones.data.price_zones} onChanged={refreshAll} onError={onError} /> : null}

      {addingTier && editable ? <TierForm eventId={detail.event.id} nextSort={detail.pricing_tiers.length + 1} timezone={detail.venue.timezone} onDone={() => { setAddingTier(false); refresh(); }} onCancel={() => setAddingTier(false)} onError={onError} /> : null}
      <Panel
        title="Pricing tiers"
        description="Optional stages: early bird, tier 1, day of event. A tier applies inside its dates and, if it has a quantity, until that many tickets have sold."
        actions={editable && !addingTier ? <Button onClick={() => setAddingTier(true)}>Add a tier</Button> : null}
      >
        <Table headers={['Tier', 'Window', 'Quantity', '']}>
          {detail.pricing_tiers.length === 0 ? (
            <EmptyRow columns={4}>No tiers: one price throughout.</EmptyRow>
          ) : (
            detail.pricing_tiers.map((tier) =>
              editingTier === tier.id ? (
                <tr key={tier.id} className="border-b border-line last:border-0">
                  <td colSpan={4} className="px-5 py-3">
                    <TierForm eventId={detail.event.id} existing={tier} nextSort={tier.sort_order} timezone={detail.venue.timezone} onDone={() => { setEditingTier(null); refresh(); }} onCancel={() => setEditingTier(null)} onError={onError} inline />
                  </td>
                </tr>
              ) : (
                <tr key={tier.id} className="border-b border-line last:border-0">
                  <td className="px-5 py-3 font-medium text-ink-900">
                    {tier.name} <span className="text-xs text-ink-400">{tier.code} · #{tier.sort_order}</span>
                  </td>
                  <td className="px-5 py-3 text-sm text-ink-600">
                    {tier.starts_at ?? 'open'} → {tier.ends_at ?? 'open'}
                  </td>
                  <td className="tabular px-5 py-3 text-sm text-ink-600">{tier.quantity_limit ?? '—'}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {editable ? (
                      <>
                        <Button onClick={() => setEditingTier(tier.id)}>Edit</Button>
                        <Button variant="danger" className="ml-2" onClick={() => removeTier.mutate(tier.id)}>
                          Delete
                        </Button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ),
            )
          )}
        </Table>
      </Panel>
    </div>
  );
}

function assignmentSummary(zone: PriceZoneRow): string {
  const a = zone.assignments ?? [];
  if (a.length === 0) return 'nothing yet';
  const count = (key: keyof (typeof a)[number]) => a.filter((x) => x[key]).length;
  const parts = [
    [count('section_id'), 'section'],
    [count('row_id'), 'row'],
    [count('table_id'), 'table'],
    [count('seat_id'), 'seat'],
    [count('ga_area_id'), 'GA area'],
  ] as const;
  return parts
    .filter(([n]) => n > 0)
    .map(([n, label]) => `${n} ${label}${n === 1 ? '' : 's'}`)
    .join(', ');
}

interface FormProps {
  eventId: string;
  onDone: () => void;
  onCancel: () => void;
  onError: PanelProps['onError'];
  inline?: boolean;
}

function TicketTypeForm({ eventId, existing, onDone, onCancel, onError, inline }: FormProps & { existing?: TicketTypeRow }) {
  const [code, setCode] = useState(existing?.code ?? 'ADULT');
  const [name, setName] = useState(existing?.name ?? 'Adult');
  const [category, setCategory] = useState(existing?.category ?? 'ADULT');
  const [channels, setChannels] = useState<string[]>(existing?.channels ?? []);
  const [min, setMin] = useState(String(existing?.min_per_order ?? 1));
  const [max, setMax] = useState(existing?.max_per_order?.toString() ?? '');
  const [comp, setComp] = useState(existing?.is_comp_type === 1);
  const [accessCode, setAccessCode] = useState(existing?.requires_access_code === 1);
  // A timed ticket admits at a set time rather than on a date (D-151). The API
  // has taken this since migration 0037; this form never sent it, so no event
  // could be given one and the timed-entry panel had nothing to build against.
  const [timedEntry, setTimedEntry] = useState(existing?.timed_entry === 1);
  const [active, setActive] = useState(existing ? existing.active === 1 : true);
  const [sort, setSort] = useState(String(existing?.sort_order ?? 0));
  const save = useMutation({
    mutationFn: () => {
      const input = {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        category,
        channels,
        min_per_order: Number(min || 1),
        max_per_order: max ? Number(max) : null,
        is_comp_type: comp,
        requires_access_code: accessCode,
        timed_entry: timedEntry,
        sort_order: Number(sort || 0),
      };
      return existing ? eventEndpoints.updateTicketType(eventId, existing.id, { ...input, active }) : eventEndpoints.createTicketType(eventId, input);
    },
    onSuccess: onDone,
    onError,
  });
  const body = (
    <form
      className="flex flex-wrap items-end gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Code" htmlFor="tt-code">
        <input id="tt-code" value={code} onChange={(e) => setCode(e.target.value)} className={`w-28 ${INPUT}`} />
      </Field>
      <Field label="Name" htmlFor="tt-name">
        <input id="tt-name" value={name} onChange={(e) => setName(e.target.value)} className={`w-48 ${INPUT}`} />
      </Field>
      <Field label="Category" htmlFor="tt-category">
        <select id="tt-category" value={category} onChange={(e) => setCategory(e.target.value)} className={INPUT}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {humanize(c)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Min / max per order" htmlFor="tt-min">
        <div className="flex items-center gap-1">
          <input id="tt-min" value={min} onChange={(e) => setMin(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-16 ${INPUT}`} />
          <span className="text-ink-400">–</span>
          <input aria-label="max per order" value={max} onChange={(e) => setMax(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="∞" className={`tabular w-16 ${INPUT}`} />
        </div>
      </Field>
      <Field label="Order" htmlFor="tt-sort">
        <input id="tt-sort" value={sort} onChange={(e) => setSort(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-14 ${INPUT}`} />
      </Field>
      <Field label="Channels (none = all)" htmlFor="tt-channels">
        <div id="tt-channels" className="flex flex-wrap gap-2 pb-1">
          {CHANNELS.map((ch) => (
            <label key={ch} className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={channels.includes(ch)} onChange={(e) => setChannels(e.target.checked ? [...channels, ch] : channels.filter((x) => x !== ch))} />
              {humanize(ch)}
            </label>
          ))}
        </div>
      </Field>
      <label className="flex items-center gap-2 pb-1.5 text-sm">
        <input type="checkbox" checked={comp} onChange={(e) => setComp(e.target.checked)} /> comp type
      </label>
      <label className="flex items-center gap-2 pb-1.5 text-sm">
        <input type="checkbox" checked={accessCode} onChange={(e) => setAccessCode(e.target.checked)} /> needs access code
      </label>
      <label className="flex items-center gap-2 pb-1.5 text-sm" title="Admits at a set entry time. Build the times on the Timed entry tab.">
        <input type="checkbox" checked={timedEntry} onChange={(e) => setTimedEntry(e.target.checked)} /> timed entry
      </label>
      {existing ? (
        <label className="flex items-center gap-2 pb-1.5 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> active
        </label>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={!code.trim() || !name.trim() || save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save' : 'Add ticket type'}
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
  return inline ? body : <Panel title="Add a ticket type"><div className="px-5 py-4">{body}</div></Panel>;
}

function ZoneForm({ eventId, existing, nextColour, onDone, onCancel, onError, inline }: FormProps & { existing?: PriceZoneRow; nextColour: string }) {
  const [code, setCode] = useState(existing?.code ?? 'P1');
  const [name, setName] = useState(existing?.name ?? 'Price level 1');
  const [color, setColor] = useState(existing?.color_hex ?? nextColour);
  const [sort, setSort] = useState(String(existing?.sort_order ?? 0));
  const save = useMutation({
    mutationFn: () => {
      const input = { code: code.trim().toUpperCase(), name: name.trim(), color_hex: color, sort_order: Number(sort || 0) };
      return existing ? eventEndpoints.updatePriceZone(eventId, existing.id, input) : eventEndpoints.createPriceZone(eventId, input);
    },
    onSuccess: onDone,
    onError,
  });
  const body = (
    <form
      className="flex flex-wrap items-end gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Code" htmlFor="pz-code">
        <input id="pz-code" value={code} onChange={(e) => setCode(e.target.value)} className={`w-24 ${INPUT}`} />
      </Field>
      <Field label="Name" htmlFor="pz-name">
        <input id="pz-name" value={name} onChange={(e) => setName(e.target.value)} className={`w-48 ${INPUT}`} />
      </Field>
      <Field label="Color" htmlFor="pz-color">
        <input id="pz-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-12 rounded border border-line" />
      </Field>
      <Field label="Order" htmlFor="pz-sort">
        <input id="pz-sort" value={sort} onChange={(e) => setSort(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-14 ${INPUT}`} />
      </Field>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={!code.trim() || !name.trim() || save.isPending}>
          {existing ? 'Save' : 'Add zone'}
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
  return inline ? body : <Panel title="Add a price zone"><div className="px-5 py-4">{body}</div></Panel>;
}

function TierForm({ eventId, existing, nextSort, timezone, onDone, onCancel, onError, inline }: FormProps & { existing?: PricingTierRow; nextSort: number; timezone: string }) {
  const [code, setCode] = useState(existing?.code ?? 'EARLY_BIRD');
  const [name, setName] = useState(existing?.name ?? 'Early bird');
  const [sort, setSort] = useState(String(existing?.sort_order ?? nextSort));
  const [starts, setStarts] = useState(utcToZoned(existing?.starts_at, timezone));
  const [ends, setEnds] = useState(utcToZoned(existing?.ends_at, timezone));
  const [quantity, setQuantity] = useState(existing?.quantity_limit?.toString() ?? '');
  const save = useMutation({
    mutationFn: () => {
      const input = { code: code.trim().toUpperCase(), name: name.trim(), sort_order: Number(sort || 1), starts_at: zonedToUtc(starts, timezone), ends_at: zonedToUtc(ends, timezone), quantity_limit: quantity ? Number(quantity) : null };
      return existing ? eventEndpoints.updateTier(eventId, existing.id, input) : eventEndpoints.createTier(eventId, input);
    },
    onSuccess: onDone,
    onError,
  });
  const body = (
    <form
      className="flex flex-wrap items-end gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Code" htmlFor="tier-code">
        <input id="tier-code" value={code} onChange={(e) => setCode(e.target.value)} className={`w-32 ${INPUT}`} />
      </Field>
      <Field label="Name" htmlFor="tier-name">
        <input id="tier-name" value={name} onChange={(e) => setName(e.target.value)} className={`w-40 ${INPUT}`} />
      </Field>
      <Field label="Order" htmlFor="tier-sort">
        <input id="tier-sort" value={sort} onChange={(e) => setSort(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-14 ${INPUT}`} />
      </Field>
      <Field label={`Starts (${timezone})`} htmlFor="tier-starts">
        <input id="tier-starts" type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} className={INPUT} />
      </Field>
      <Field label="Ends" htmlFor="tier-ends">
        <input id="tier-ends" type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} className={INPUT} />
      </Field>
      <Field label="Quantity limit" htmlFor="tier-qty">
        <input id="tier-qty" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
      </Field>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={!code.trim() || !name.trim() || save.isPending}>
          {existing ? 'Save' : 'Add tier'}
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
  return inline ? body : <Panel title="Add a pricing tier" description="Tiers run in order. Leave the dates empty for a tier that simply follows the previous one."><div className="px-5 py-4">{body}</div></Panel>;
}

/**
 * The zone map: every seat colored by the zone it resolves to right now.
 * Select seats (click, shift-click, shift-drag) and assign them, their rows,
 * or whole sections to a zone. Assignments are additive and specific, so
 * painting a few seats leaves the rest of the section where it was.
 */
function ZoneMap({ detail, zones, onChanged, onError }: { detail: PanelProps['detail']; zones: PriceZoneRow[]; onChanged: () => void; onError: PanelProps['onError'] }) {
  const queryClient = useQueryClient();
  const manifestIds = [...new Set(detail.performances.map((p) => p.manifest_id))];
  const [manifestId, setManifestId] = useState(manifestIds[0] ?? '');
  const [zoneId, setZoneId] = useState(zones[0]?.id ?? '');
  const layout = useQuery<ManifestLayout>({ queryKey: ['manifest-layout', manifestId], queryFn: () => endpoints.manifestLayout(manifestId), enabled: Boolean(manifestId) });
  const effective = useQuery({ queryKey: ['effective-zones', detail.event.id, manifestId], queryFn: () => eventEndpoints.effectiveZones(detail.event.id, manifestId), enabled: Boolean(manifestId) });
  const selection = useSeatSelection();
  const colorOf = useMemo(() => new Map(zones.map((z) => [z.id, z.color_hex ?? '#d9d9d9'])), [zones]);
  const seatFills = useMemo(() => {
    const fills: Record<string, string> = {};
    for (const [seatId, zone] of Object.entries(effective.data?.seats ?? {})) fills[seatId] = zone ? (colorOf.get(zone) ?? '#d9d9d9') : '#ffffff';
    return fills;
  }, [effective.data, colorOf]);

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['effective-zones', detail.event.id, manifestId] });
    onChanged();
  };
  const assign = useMutation({
    mutationFn: ({ targets, mode, toZone }: { targets: Record<string, string>[]; mode: 'add' | 'remove'; toZone: string }) =>
      eventEndpoints.setZoneAssignments(detail.event.id, toZone, targets, null, mode),
    onSuccess: () => {
      selection.clear();
      done();
    },
    onError,
  });

  const selectedSeats = [...selection.selected];
  const selectedRows = [...new Set(selectedSeats.map((id) => layout.data?.seats.find((s) => s.id === id)?.row_id).filter((r): r is string => Boolean(r)))];
  const sectionsOf = [...new Set(selectedSeats.map((id) => layout.data?.seats.find((s) => s.id === id)?.section_id).filter((s): s is string => Boolean(s)))];
  const zone = zones.find((z) => z.id === zoneId);

  /** Seat-level and row-level rows that sit under the selection, in any zone: what "clear overrides" removes. */
  const overrides = zones.flatMap((z) =>
    (z.assignments ?? [])
      .filter((a) => !a.performance_id && ((a.seat_id && selection.selected.has(a.seat_id)) || (a.row_id && selectedRows.includes(a.row_id))))
      .map((a) => ({ zone: z.id, target: a.seat_id ? { seat_id: a.seat_id } : { row_id: a.row_id! } })),
  );
  const clearOverrides = useMutation({
    mutationFn: async () => {
      const byZone = new Map<string, Record<string, string>[]>();
      for (const o of overrides) byZone.set(o.zone, [...(byZone.get(o.zone) ?? []), o.target]);
      for (const [z, targets] of byZone) await eventEndpoints.setZoneAssignments(detail.event.id, z, targets, null, 'remove');
    },
    onSuccess: () => {
      selection.clear();
      done();
    },
    onError,
  });

  const geometry = layout.data as unknown as ManifestGeometry | undefined;
  const gaAreas = layout.data?.ga_areas ?? [];
  /*
   * A room can have nothing to paint (D-187).
   *
   * Haunted Auditorium is general admission and timed entry: no seats at all.
   * The painter drew an empty canvas anyway and put the GA areas below the
   * legend as a footnote, so "paint zones on the map" led to a blank rectangle
   * and the one control that could have helped was under it, looking like a
   * caption. Without a zone the initializer refuses, and the message said to go
   * and paint seats.
   */
  const paintable = (geometry?.seats.length ?? 0) > 0;
  const unzonedGa = gaAreas.filter((a) => !effective.data?.ga_areas[a.id]);

  return (
    <Panel
      title="Zone map"
      description={
        paintable
          ? 'Colored by the zone each seat resolves to. White seats have no zone yet and cannot be priced.'
          : 'This layout is all general admission, so there are no seats to paint. Give each area a zone below; an area with none cannot be priced.'
      }
    >
      <div className="space-y-3 px-5 py-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {manifestIds.length > 1 ? (
            <select value={manifestId} onChange={(e) => setManifestId(e.target.value)} className={INPUT} aria-label="Layout">
              {manifestIds.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          ) : null}
          <label className="flex items-center gap-2">
            Zone
            <select value={zoneId} onChange={(e) => setZoneId(e.target.value)} className={INPUT}>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </label>
          {zone ? <span className="inline-block h-4 w-4 rounded-sm border border-line" style={{ background: zone.color_hex ?? '#d9d9d9' }} /> : null}
        </div>
        {/* Seat, row and section assignment: nothing to act on without seats. */}
        <div className={`flex-wrap items-center gap-3 text-sm ${paintable ? 'flex' : 'hidden'}`}>
          <Button variant="primary" disabled={selectedSeats.length === 0 || assign.isPending} onClick={() => assign.mutate({ targets: selectedSeats.map((seat_id) => ({ seat_id })), mode: 'add', toZone: zoneId })}>
            {selectedSeats.length} seat{selectedSeats.length === 1 ? '' : 's'} → {zone?.name ?? 'zone'}
          </Button>
          <Button disabled={selectedRows.length === 0 || assign.isPending} onClick={() => assign.mutate({ targets: selectedRows.map((row_id) => ({ row_id })), mode: 'add', toZone: zoneId })}>
            whole row{selectedRows.length === 1 ? '' : 's'} ({selectedRows.length}) → {zone?.name ?? 'zone'}
          </Button>
          <Button disabled={sectionsOf.length === 0 || assign.isPending} onClick={() => assign.mutate({ targets: sectionsOf.map((section_id) => ({ section_id })), mode: 'add', toZone: zoneId })}>
            whole section{sectionsOf.length === 1 ? '' : 's'} ({sectionsOf.length}) → {zone?.name ?? 'zone'}
          </Button>
          <Button disabled={overrides.length === 0 || clearOverrides.isPending} onClick={() => clearOverrides.mutate()} title="Remove seat- and row-level assignments under the selection so the section rule applies">
            Clear {overrides.length} override{overrides.length === 1 ? '' : 's'}
          </Button>
          {selectedSeats.length > 0 ? <Button onClick={selection.clear}>Clear selection</Button> : null}
        </div>
        {!geometry ? (
          <p className="text-sm text-ink-400">Add {dateWords().a} first; zones cover the layout it sells into.</p>
        ) : paintable ? (
          <SeatMapView layout={geometry} mode="edit" backgroundUrl={layout.data?.manifest.background_url ?? null} seatFills={seatFills} selectedSeatIds={selection.selected} onSeatClick={selection.clickSeat} onMarqueeSelect={selection.marquee} className="overflow-hidden rounded border border-line" />
        ) : null}
        <div className={`flex-wrap items-center gap-3 text-xs text-ink-600 ${paintable ? 'flex' : 'hidden'}`}>
          <span className="font-semibold uppercase tracking-wide text-ink-400">Legend</span>
          {zones.map((z) => (
            <span key={z.id} className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded-sm border border-line" style={{ background: z.color_hex ?? '#d9d9d9' }} /> {z.name}
            </span>
          ))}
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded-sm border border-line bg-white" /> no zone
          </span>
        </div>
        {gaAreas.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={unzonedGa.length > 0 ? 'font-semibold text-bad' : 'text-ink-400'}>
              {unzonedGa.length > 0 ? `${unzonedGa.length} GA area${unzonedGa.length === 1 ? '' : 's'} need a zone:` : 'GA areas:'}
            </span>
            {gaAreas.map((a) => {
              const current = effective.data?.ga_areas[a.id] ?? null;
              return (
                <label key={a.id} className="flex items-center gap-1 rounded border border-line px-2 py-1">
                  <span className="inline-block h-3 w-3 rounded-sm border border-line" style={{ background: current ? (colorOf.get(current) ?? '#d9d9d9') : '#fff' }} />
                  {a.name}
                  <select
                    value={current ?? ''}
                    onChange={(e) => {
                      if (e.target.value) assign.mutate({ targets: [{ ga_area_id: a.id }], mode: 'add', toZone: e.target.value });
                      else if (current) assign.mutate({ targets: [{ ga_area_id: a.id }], mode: 'remove', toZone: current });
                    }}
                    className={INPUT}
                    aria-label={`Zone for ${a.name}`}
                  >
                    <option value="">— none —</option>
                    {zones.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                </label>
              );
            })}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
